import React from "react";
import { Dialog } from "@mui/material";
import {
  ButtonPrimary,
  ButtonSecondary,
  HStack,
  VStack,
} from "../../components/utils";
import { formatDisplayDate } from "../../utils/date";
import StatusLabel from "../esopOverview/StatusLabel";
import { useEmployees } from "../../queries/employees";
import InfoBox from "../../components/shared/InfoBox";
import { getRequestStatus } from "./PendingRequestsApproval";

// ── DTOs ─────────────────────────────────────────────────────────────────────
export interface ResignationRequestData {
  employeeId: string;
  resignationDate: string;
  lastDay: string;
  status: string;
}

export interface InternalTransferRequestData {
  oldEmployeeId: string;
  newEmployeeId?: string;
  hrId: string;
  oldHrId?: string;
  newHrId?: string;
  configId?: string;
  config?: {
    skipDateValidation?: boolean;
    dateGapDays?: number;
  };
  oldEmployeeDetailsToUpdate?: Record<string, any>;
  newEmployeeDetailsToCreate?: Record<string, any>;
}

// ── Review & Approve Modal ───────────────────────────────────────────────────
function ReviewApproveModal({
  open,
  request,
  isApproving,
  isRejecting,
  onApprove,
  onReject,
  onClose,
}: {
  open: boolean;
  request: any | null;
  isApproving: boolean;
  isRejecting?: boolean;
  onApprove: (id: string) => void;
  onReject?: (id: string) => void;
  onClose: () => void;
}) {
  const { data: employees } = useEmployees();

  if (!request) return null;

  const requestType = (request.requestType ?? "").toUpperCase();
  const data = request.data ?? {};

  const isApproveEnabled = (() => {
    if (requestType === "RESIGNATION" || requestType === "SEPARATION") {
      const d = data as ResignationRequestData;
      return !!(d.status && d.resignationDate && d.lastDay);
    }
    return true;
  })();

  const renderDetails = () => {
    if (requestType === "RESIGNATION" || requestType === "SEPARATION") {
      const d = data as ResignationRequestData;
      const emp = (employees ?? []).find((e) => e.id === d.employeeId);
      const rows = [
        { label: "Name", value: emp?.employeeName },
        { label: "HR ID", value: emp?.employeeIdentificationString },
        {
          label: "Resignation Date",
          value: d.resignationDate
            ? formatDisplayDate(new Date(d.resignationDate))
            : undefined,
        },
        {
          label: "Last Day",
          value: d.lastDay ? formatDisplayDate(new Date(d.lastDay)) : undefined,
        },
        { label: "Status", value: d.status },
      ].filter((r) => r.value);

      return (
        <table className="w-full text-xs border border-collapse border-gray-200 table-auto">
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className="hover:bg-gray-50">
                <td className="px-3 py-2 text-gray-500 border border-gray-200 w-40 whitespace-nowrap">
                  {r.label}
                </td>
                <td className="px-3 py-2 font-medium text-gray-dark border border-gray-200 break-all">
                  {r.value ?? "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    if (requestType === "INTERNAL_TRANSFER") {
      const d = data as InternalTransferRequestData;
      const oldEmp = (employees ?? []).find((e) => e.id === d.oldEmployeeId);
      const newEmp =
        d.newEmployeeId || d.newHrId
          ? (employees ?? []).find(
              (e) =>
                e.id === d.newEmployeeId ||
                e.employeeIdentificationString === d.newHrId
            )
          : undefined;

      const oldDetails = d.oldEmployeeDetailsToUpdate ?? {};
      const newDetails = d.newEmployeeDetailsToCreate ?? {};

      const comparisonRows = [
        {
          label: "HR ID",
          old:
            d.oldHrId ??
            oldEmp?.employeeIdentificationString ??
            d.oldEmployeeId ??
            "—",
          new:
            d.newHrId ??
            newEmp?.employeeIdentificationString ??
            d.newEmployeeId ??
            "—",
        },
        {
          label: "Name",
          old: oldDetails.employeeName ?? oldEmp?.employeeName ?? "—",
          new: newDetails.name ?? newEmp?.employeeName ?? "—",
        },
        {
          label: "Email",
          old: oldDetails.email ?? oldEmp?.email ?? "—",
          new: newDetails.email ?? newEmp?.email ?? "—",
        },
        {
          label: "Personal Email",
          old:
            oldDetails.personalEmail ?? (oldEmp as any)?.personalEmail ?? "—",
          new:
            newDetails.personalEmail ?? (newEmp as any)?.personalEmail ?? "—",
        },
        {
          label: "Joining Date",
          old:
            oldEmp?.dateOfJoin ??
            oldDetails.dateOfJoin ??
            oldDetails.dateOfJoining
              ? formatDisplayDate(
                  new Date(
                    oldEmp?.dateOfJoin ??
                      oldDetails.dateOfJoin ??
                      oldDetails.dateOfJoining ??
                      ""
                  )
                )
              : "—",
          new:
            newDetails.dateOfJoin ??
            newDetails.dateOfJoining ??
            newEmp?.dateOfJoin
              ? formatDisplayDate(
                  new Date(
                    newDetails.dateOfJoin ??
                      newDetails.dateOfJoining ??
                      newEmp?.dateOfJoin ??
                      ""
                  )
                )
              : "—",
        },
        ...(oldDetails.groupDateOfJoining ?? newDetails.groupDateOfJoining
          ? [
              {
                label: "Group Joining Date",
                old: oldDetails.groupDateOfJoining
                  ? formatDisplayDate(new Date(oldDetails.groupDateOfJoining))
                  : "—",
                new: newDetails.groupDateOfJoining
                  ? formatDisplayDate(new Date(newDetails.groupDateOfJoining))
                  : "—",
              },
            ]
          : []),
        {
          label: "No. of Grants",
          old: String(oldEmp?.noOfGrants ?? 0),
          new: String(newDetails?.noOfGrants ?? newEmp?.noOfGrants ?? 0),
        },
      ];

      return (
        <VStack className="gap-3">
          <table className="w-full text-xs border border-collapse border-gray-200 table-auto">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-3 py-2 text-left text-gray-500 font-medium border border-gray-200 w-32">
                  Field
                </th>
                <th className="px-3 py-2 text-left text-gray-500 font-medium border border-gray-200">
                  From (Old)
                </th>
                <th className="px-3 py-2 text-left text-gray-500 font-medium border border-gray-200">
                  To (New)
                </th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((r) => (
                <tr key={r.label} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-gray-500 border border-gray-200 whitespace-nowrap">
                    {r.label}
                  </td>
                  <td className="px-3 py-2 font-medium text-gray-dark border border-gray-200 break-all">
                    {r.old}
                  </td>
                  <td className="px-3 py-2 font-medium text-gray-dark border border-gray-200 break-all">
                    {r.new}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <InfoBox
            message="All grants belonging to the old employee
              will be transferred to the new employee upon approval."
          />
        </VStack>
      );
    }

    if (requestType === "EXERCISE") {
      return (
        <table className="w-full text-xs border border-collapse border-gray-200 table-auto">
          <tbody>
            <tr className="hover:bg-gray-50">
              <td className="px-3 py-2 text-gray-500 border border-gray-200 w-40 whitespace-nowrap">
                Exercise Date
              </td>
              <td className="px-3 py-2 font-medium text-gray-dark border border-gray-200 break-all">
                {data.exerciseDate
                  ? formatDisplayDate(new Date(data.exerciseDate))
                  : "—"}
              </td>
            </tr>
          </tbody>
        </table>
      );
    }

    // fallback
    return (
      <p className="text-xs text-gray-500 py-2">
        {data.subject ?? "No additional details available."}
      </p>
    );
  };

  return (
    <Dialog
      open={open}
      maxWidth={requestType === "INTERNAL_TRANSFER" ? "md" : "sm"}
      fullWidth
      onClose={onClose}
    >
      <div className="px-10 text-lg font-medium border-b py-7">
        <h6 className="flex justify-between">
          Review Request
          <span onClick={onClose} className="cursor-pointer">
            X
          </span>
        </h6>
      </div>
      <VStack className="px-10 py-4 gap-4">
        {/* Meta info */}
        <VStack className="gap-2">
          <HStack className="items-center gap-4">
            <h5 className="text-sm font-medium text-[#030303]">
              {(request.requestType ?? "—").replace(/_/g, " ")}
            </h5>
            <StatusLabel state={getRequestStatus(request.status)} />
          </HStack>
          <HStack className="flex-wrap">
            {[
              { field: "Raised By", value: request.createdBy },
              {
                field: "Raised On",
                value: request.createdAt
                  ? formatDisplayDate(new Date(request.createdAt))
                  : undefined,
              },
              {
                field: "Last Updated",
                value: request.updatedAt
                  ? formatDisplayDate(new Date(request.updatedAt))
                  : undefined,
              },
            ]
              .filter((r) => r.value)
              .map(({ field, value }) => (
                <HStack key={field} className="w-1/2 p-1">
                  <span className="w-1/2 text-sm font-normal text-gray-400">
                    {field}
                  </span>
                  <span className="w-1/2 text-xs3 text-[#030303] font-medium">
                    {value}
                  </span>
                </HStack>
              ))}
          </HStack>
        </VStack>

        {/* Type-specific details */}
        <VStack className="gap-1">
          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
            Details
          </p>
          {renderDetails()}
        </VStack>

        {/* Validation warning */}
        {!isApproveEnabled &&
          (requestType === "RESIGNATION" || requestType === "SEPARATION") && (
            <p className="text-xs text-orange-600 bg-orange-50 border border-orange-200 rounded px-3 py-2">
              All fields (Status, Resignation Date, Last Day) are required to
              approve this request.
            </p>
          )}

        {/* Actions */}
        <HStack className="justify-between gap-3 pt-2">
          <ButtonSecondary
            onClick={onClose}
            className="text-gray-400 bg-slate-50"
          >
            Cancel
          </ButtonSecondary>
          <HStack className="gap-2">
            {requestType === "INTERNAL_TRANSFER" &&
              ["PENDING", "REQUESTED"].includes(
                (request.status ?? "").toUpperCase()
              ) &&
              onReject && (
                <ButtonSecondary
                  loading={isRejecting}
                  className="text-red-500 border-red-300 bg-red-50 hover:bg-red-100"
                  onClick={() => onReject(request.id)}
                >
                  Reject
                </ButtonSecondary>
              )}
            {["PENDING", "REQUESTED"].includes(
              (request.status ?? "").toUpperCase()
            ) && (
              <ButtonPrimary
                loading={isApproving}
                disabled={!isApproveEnabled}
                onClick={() => {
                  if (
                    ["PENDING", "REQUESTED"].includes(
                      (request.status ?? "").toUpperCase()
                    ) &&
                    isApproveEnabled
                  ) {
                    onApprove(request.id);
                  }
                }}
              >
                Approve
              </ButtonPrimary>
            )}
          </HStack>
        </HStack>
      </VStack>
    </Dialog>
  );
}

export default ReviewApproveModal;
